import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { USER_TENANT_MEMBERSHIPS_API_ENDPOINTS as cfg } from './config';
import type { GetUserMembershipResponse, ListUserMembershipsResponse } from './types';
import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query';

const tags = ['UserMemberships'] as const;
type TagTypes = (typeof tags)[number];

type Definitions = DefinitionsFromApi<typeof generatedApi>;
type UpdatedDefinitions = Omit<
  Definitions,
  typeof cfg.listUserMemberships.generated | typeof cfg.getUserMembership.generated
> & {
  [cfg.listUserMemberships.generated]: OverrideResultType<
    Definitions[typeof cfg.listUserMemberships.generated],
    ListUserMembershipsResponse['data']
  >;
  [cfg.getUserMembership.generated]: OverrideResultType<
    Definitions[typeof cfg.getUserMembership.generated],
    GetUserMembershipResponse['data']
  >;
};

/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 * All endpoint references are derived from the central definitions in ./config.ts.
 */
export const userTenantMembershipsApi = generatedApi.enhanceEndpoints<TagTypes, UpdatedDefinitions>(
  {
    addTagTypes: tags,
    endpoints: {
      [cfg.addUserMembership.generated]: {
        invalidatesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
      },
      [cfg.listUserMemberships.generated]: {
        providesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
        transformResponse: (
          response: ListUserMembershipsResponse,
          _arg,
          _meta,
        ): ListUserMembershipsResponse['data'] => {
          return response.data;
        },
      },
      [cfg.getUserMembership.generated]: {
        providesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
        transformResponse: (
          response: GetUserMembershipResponse,
          _arg,
          _meta,
        ): GetUserMembershipResponse['data'] => {
          return response.data;
        },
      },
      [cfg.removeUserFromTenant.generated]: {
        invalidatesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
      },
      [cfg.updateUserMembership.generated]: {
        invalidatesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
      },
      [cfg.removeUserMembership.generated]: {
        invalidatesTags: (_, __, arg) => [{ type: 'UserMemberships', id: arg.userId }],
      },
    },
  },
);

// Re-export hooks and endpoint maps via helpers to match the pattern used in data-tenants
export const hookExports: HookExports<typeof cfg, typeof userTenantMembershipsApi> =
  createHookExports(cfg, userTenantMembershipsApi);
export const userTenantMembershipApiEndpoints = createEndpointExports(
  cfg,
  userTenantMembershipsApi,
);
